import Highcharts, { Options } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo, useState } from "react";
import { Card, CardBody, CardSubtitle, CardTitle } from "reactstrap";
import { Checkbox, McDropdown, McInput, McTypeahead } from "../../../components";
import { MCApplicationsUrlEnum } from "../../../mc-shared/sharedConstants";
import { ISSUE_REGISTER_NAME_OPTIONS } from "../issuesApp/issueConstants";
import { useGetAllProjectsForStatsPageQuery, useGetWeeklyStatsQuery } from "./statsRTK";

interface IChartPoint {
  x: number;
  y: number;
}

export interface IStat {
  logDate: Date;
  count: number;
}

const WeeklyUserChart = () => {
  const [fromDate, setFromDate] = useState<Date>(null);
  const [toDate, setToDate] = useState<Date>(new Date());
  const [projectNo, setProjectNo] = useState<number>(null);
  const [toggleUniqueUsers, setToggleUniqueUsers] = useState<boolean>(true);
  const [appName, setAppName] = useState<string>("");
  const [createdFromTemplate, setCreatedFromTemplate] = useState<string>("");

  const appOptions = useMemo(
    () => [
      { name: "Dashboard", value: MCApplicationsUrlEnum.DASHBOARD },
      { name: "Møter", value: MCApplicationsUrlEnum.MEETING },
      { name: "Rapporter", value: MCApplicationsUrlEnum.REPORT },
      { name: "Register", value: MCApplicationsUrlEnum.ISSUE },
      { name: "Sjekkliste", value: MCApplicationsUrlEnum.CHECKLIST },
    ],
    [],
  );

  const issueTemplateOptions = useMemo(
    () =>
      ISSUE_REGISTER_NAME_OPTIONS.map((template) => ({
        name: template.type,
        value: template.type,
      })),
    [],
  );

  const { data: res } = useGetWeeklyStatsQuery({
    fromDate: fromDate?.toISOString(),
    toDate: toDate?.toISOString(),
    projectNo: projectNo,
    toggleUniqueUsers: toggleUniqueUsers === true ? "YES" : "NO",
    appName: appName,
    issueBoardTemplate: createdFromTemplate,
  });

  const { data: projects } = useGetAllProjectsForStatsPageQuery("");

  const totalUsers: IChartPoint[] = res?.map((logItem) => {
    return {
      x: new Date(logItem.logDate).getTime(),
      y: logItem.count,
    };
  });

  const highcartSeries: any[] = [
    {
      name: toggleUniqueUsers === false ? "Total appstarter" : "Total unike brukere",
      data: totalUsers,
      pointInterval: 24 * 3600 * 1000 * 7, // one week,
      color: "orange",
    },
  ];

  const options: Options = {
    accessibility: { enabled: false }, //to remove warning
    title: {
      text: toggleUniqueUsers === false ? "Appstarter hver uke" : "Unike brukere hver uke",
    },
    chart: {
      type: "area",
    },
    subtitle: {
      text: "",
    },
    yAxis: {
      title: {
        text: toggleUniqueUsers === false ? "Antall bruker" : " Antall appstarter ",
      },
    },
    xAxis: {
      type: "datetime", // Show dates
      labels: {
        autoRotation: [-90],
        format: "{value:%d-%m-%Y}", // Format for the weeks
      },
      tickInterval: 7 * 24 * 3600 * 1000,
    },
    plotOptions: {
      column: {
        pointWidth: 15, // Width of the column
        pointPlacement: "on",
      },
    },
    series: highcartSeries || [],
  };

  return (
    <Card className="m-3">
      <div className="d-flex m-2">
        <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
          <h5 className="truncateHeaderText mb-0">Fra</h5>
          <McInput
            type="date"
            onChange={(e) => setFromDate(e.target.value !== "" ? new Date(e.target.value) : null)}
            value={fromDate != null ? fromDate.toISOString() : null}
          />
        </div>
        <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
          <h5 className="truncateHeaderText mb-0">Til</h5>
          <McInput
            type="date"
            onChange={(e) => setToDate(e.target.value !== "" ? new Date(e.target.value) : new Date())}
            value={toDate != null ? toDate.toISOString() : null}
          />
        </div>
        <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
          <h5 className="truncateHeaderText mb-0">Prosjekt</h5>
          <McTypeahead
            id="issuepnstypeahead"
            clearButton
            labelKey={"projectName"}
            options={projects || []}
            onChange={(project) => {
              setProjectNo(project[0]?.projectNo !== null && project.length !== 0 ? project[0].projectNo : null);
            }}
            allowNew
          />
        </div>
        <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
          <h5 className="truncateHeaderText mb-0">App</h5>
          <McDropdown
            onClear={appName !== "" ? () => setAppName("") : null}
            displayKey="name"
            options={appOptions}
            currentValue={appOptions.find((a) => a.value === appName) || { name: "", value: "" }}
            onChange={(_, v) => setAppName(v.value)}
          />
        </div>
        {appName === MCApplicationsUrlEnum.ISSUE && (
          <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
            <h5 className="truncateHeaderText mb-0">Registermal</h5>
            <McDropdown
              onClear={createdFromTemplate !== "" ? () => setCreatedFromTemplate("") : null}
              displayKey="name"
              options={issueTemplateOptions}
              currentValue={
                issueTemplateOptions.find((a) => a.value === createdFromTemplate) || { name: "", value: "" }
              }
              onChange={(_, v) => setCreatedFromTemplate(v.value)}
            />
          </div>
        )}
        <div className="d-flex flex-column mr-4" style={{ maxWidth: "15rem", width: "100%" }}>
          <h5 className="truncateHeaderText mb-1">Unike brukere</h5>
          <Checkbox isChecked={toggleUniqueUsers} setChecked={() => setToggleUniqueUsers(!toggleUniqueUsers)} />
        </div>
      </div>
      <CardBody>
        <CardTitle tag="h3">Oversikt appstarter</CardTitle>
        <CardSubtitle tag="h6" className="mb-2 text-muted" />
        {<HighchartsReact containerProps={{ style: { height: "100%" } }} highcharts={Highcharts} options={options} />}
      </CardBody>
    </Card>
  );
};

export default WeeklyUserChart;
